import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ChevronRight, X, ArrowUp, Dices } from "lucide-react";
import axios from "axios";
import { ensureInningScores, getBatterIndex, showScoringToast } from "./utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BaserunningPhase({ game, gameId, onUpdate, setCurrentPhase, selectedRunner, setSelectedRunner, onRecordSteal }) {
  const state = game.state;
  const battingTeam = state.half === "top" ? game.away_team : game.home_team;
  const batters = battingTeam.players.filter(p => p.position !== "P");

  const baseLabels = { first: '1B', second: '2B', third: '3B' };
  const baseNames = { first: 'First', second: 'Second', third: 'Third' };
  const nextBase = { first: 'second', second: 'third', third: 'home' };
  const runners = ['third', 'second', 'first'].filter(b => state.bases[b]);

  // Steal state
  const [stealRunner, setStealRunner] = useState(null);
  const [runnerRoll, setRunnerRoll] = useState("");
  const [catcherRoll, setCatcherRoll] = useState("");
  const [runnerMgr, setRunnerMgr] = useState("");
  const [catcherMgr, setCatcherMgr] = useState("");
  const [stealResult, setStealResult] = useState(null);
  const [isRolling, setIsRolling] = useState(false);
  const rollRef = useRef(null);

  // Rundown state (triggered by steal tie)
  const [rundownBase, setRundownBase] = useState(null);
  const [rdFieldGuess1, setRdFieldGuess1] = useState("");
  const [rdFieldGuess2, setRdFieldGuess2] = useState("");
  const [rdRunnerGuess, setRdRunnerGuess] = useState("");
  const [rdFieldRoll1, setRdFieldRoll1] = useState("");
  const [rdFieldRoll2, setRdFieldRoll2] = useState("");
  const [rdRunnerRoll, setRdRunnerRoll] = useState("");
  const [rdResult, setRdResult] = useState(null);
  const [rdRolling, setRdRolling] = useState(false);
  const rdRollRef = useRef(null);

  // A runner can steal if the next base is empty (no runner blocking the path)
  const canSteal = (base) => {
    const next = nextBase[base];
    if (next === 'home') return true;
    return !state.bases[next];
  };

  const clearSteal = () => {
    setStealRunner(null);
    setRunnerRoll(""); setCatcherRoll(""); setRunnerMgr(""); setCatcherMgr("");
    setStealResult(null); setIsRolling(false);
    clearInterval(rollRef.current);
  };

  const triggerRundown = (base) => {
    setRundownBase(base);
    setStealRunner(null);
    setRunnerRoll(""); setCatcherRoll(""); setRunnerMgr(""); setCatcherMgr("");
    setStealResult(null); setIsRolling(false);
  };

  const rollSteal = () => {
    const hasManualRoll = runnerRoll !== "" && catcherRoll !== "";
    if (hasManualRoll) {
      const rTotal = (parseInt(runnerRoll) || 0) + (parseInt(runnerMgr) || 0);
      const cTotal = (parseInt(catcherRoll) || 0) + (parseInt(catcherMgr) || 0);
      if (rTotal === cTotal) { triggerRundown(stealRunner); return; }
      setStealResult(rTotal > cTotal ? 'safe' : 'out');
      return;
    }

    setIsRolling(true);
    setStealResult(null);
    rollRef.current = setInterval(() => {
      setRunnerRoll(String(Math.floor(Math.random() * 6) + 1));
      setCatcherRoll(String(Math.floor(Math.random() * 6) + 1));
    }, 60);

    setTimeout(() => {
      clearInterval(rollRef.current);
      const rRoll = Math.floor(Math.random() * 6) + 1;
      const cRoll = Math.floor(Math.random() * 6) + 1;
      setRunnerRoll(String(rRoll));
      setCatcherRoll(String(cRoll));
      const rTotal = rRoll + (parseInt(runnerMgr) || 0);
      const cTotal = cRoll + (parseInt(catcherMgr) || 0);
      setIsRolling(false);
      if (rTotal === cTotal) { triggerRundown(stealRunner); return; }
      setStealResult(rTotal > cTotal ? 'safe' : 'out');
    }, 500);
  };

  const rollRundown = () => {
    const hasManual = rdFieldRoll1 !== "" && rdFieldRoll2 !== "" && rdRunnerRoll !== "";
    const fr1 = hasManual ? (parseInt(rdFieldRoll1) || 1) : Math.floor(Math.random() * 6) + 1;
    const fr2 = hasManual ? (parseInt(rdFieldRoll2) || 1) : Math.floor(Math.random() * 6) + 1;
    const rr  = hasManual ? (parseInt(rdRunnerRoll)  || 1) : Math.floor(Math.random() * 6) + 1;

    if (hasManual) {
      resolveRundown(fr1, fr2, rr);
      return;
    }

    setRdRolling(true);
    setRdResult(null);
    rdRollRef.current = setInterval(() => {
      setRdFieldRoll1(String(Math.floor(Math.random() * 6) + 1));
      setRdFieldRoll2(String(Math.floor(Math.random() * 6) + 1));
      setRdRunnerRoll(String(Math.floor(Math.random() * 6) + 1));
    }, 60);
    setTimeout(() => {
      clearInterval(rdRollRef.current);
      const f1 = Math.floor(Math.random() * 6) + 1;
      const f2 = Math.floor(Math.random() * 6) + 1;
      const r  = Math.floor(Math.random() * 6) + 1;
      setRdFieldRoll1(String(f1));
      setRdFieldRoll2(String(f2));
      setRdRunnerRoll(String(r));
      setRdRolling(false);
      resolveRundown(f1, f2, r);
    }, 500);
  };

  const resolveRundown = (f1, f2, r) => {
    const fg1 = parseInt(rdFieldGuess1) || 0;
    const fg2 = parseInt(rdFieldGuess2) || 0;
    const rg  = parseInt(rdRunnerGuess)  || 0;
    const fieldingHit = (fg1 > 0 && (fg1 === f1 || fg1 === f2)) || (fg2 > 0 && (fg2 === f1 || fg2 === f2));
    const runnerHit   = rg > 0 && rg === r;
    if (!fieldingHit && !runnerHit) { setRdResult('repeat'); return; }
    // Fielding wins tie
    setRdResult(fieldingHit ? 'out' : 'safe');
  };

  const resetRundown = () => {
    setRdFieldGuess1(""); setRdFieldGuess2(""); setRdRunnerGuess("");
    setRdFieldRoll1(""); setRdFieldRoll2(""); setRdRunnerRoll("");
    setRdResult(null);
  };

  const applyRundown = async (outcome) => {
    const base = rundownBase;
    setRundownBase(null);
    resetRundown();
    if (outcome === 'safe') {
      if (onRecordSteal && state.bases[base]?.id) onRecordSteal(state.bases[base].id);
      await advanceRunner(base, nextBase[base]);
    } else {
      await recordOut(base);
    }
  };

  const applySteal = async (outcome) => {
    const base = stealRunner;
    const runner = state.bases[base];
    clearSteal();
    if (outcome === 'safe') {
      if (onRecordSteal && runner?.id) onRecordSteal(runner.id);
      await advanceRunner(base, nextBase[base]);
    } else {
      await recordOut(base);
    }
  };

  const advanceRunner = async (fromBase, toBase) => {
    if (toBase !== 'home' && state.bases[toBase]) {
      const occupant = state.bases[toBase];
      toast.error(`Advance ${occupant.name || 'lead runner'} first`);
      return;
    }
    const newBases = { ...state.bases };
    const runner = newBases[fromBase];
    const newState = { ...state };
    newState.inning_scores = ensureInningScores(state.inning_scores, Math.max(9, state.inning));
    let runsScored = 0;
    const scoringRunners = [];
    newBases[fromBase] = null;
    if (toBase !== 'home') { newBases[toBase] = { name: runner?.name, id: runner?.id }; }
    else { runsScored++; scoringRunners.push(runner?.name || 'Runner'); if (state.half === "bottom") newState.home_score = (newState.home_score || 0) + 1; else newState.away_score = (newState.away_score || 0) + 1; }
    if (scoringRunners.length > 0) showScoringToast(scoringRunners);
    newState.bases = newBases;
    if (runsScored > 0) { const innIdx = state.inning - 1; newState.inning_scores[innIdx][state.half] = (newState.inning_scores[innIdx][state.half] || 0) + runsScored; }
    onUpdate(newState); setSelectedRunner(null);
  };

  const recordOut = async (base) => {
    const newBases = { ...state.bases };
    const runner = newBases[base];
    newBases[base] = null;
    const newState = { ...state, bases: newBases, outs: state.outs + 1 };
    newState.inning_scores = ensureInningScores(state.inning_scores, Math.max(9, state.inning));
    const innIdx = state.inning - 1;
    let inningEnded = false;
    if (newState.outs >= 3) {
      if (newState.inning_scores[innIdx][state.half] === null) newState.inning_scores[innIdx][state.half] = 0;
      newState.outs = 0; newState.balls = 0; newState.strikes = 0;
      newState.bases = { first: null, second: null, third: null };
      if (state.half === "top") { newState.half = "bottom"; }
      else { newState.half = "top"; newState.inning = state.inning + 1; }
      inningEnded = true;
      toast.info("Side retired!");
    } else {
      toast.error(`${runner?.name || 'Runner'} is out!`);
    }
    onUpdate(newState);
    setSelectedRunner(null);
    if (inningEnded) setCurrentPhase("pitch");
  };

  const advanceAll = async () => {
    const newBases = { first: null, second: null, third: null };
    const newState = { ...state };
    newState.inning_scores = ensureInningScores(state.inning_scores, Math.max(9, state.inning));
    let runsScored = 0; const scoringRunners = [];
    for (const base of ['third', 'second', 'first']) {
      if (state.bases[base]) {
        const next = nextBase[base];
        if (next === 'home') { if (state.half === "bottom") newState.home_score += 1; else newState.away_score += 1; runsScored++; scoringRunners.push(state.bases[base]?.name || 'Runner'); }
        else { newBases[next] = { name: state.bases[base]?.name, id: state.bases[base]?.id }; }
      }
    }
    if (scoringRunners.length > 0) showScoringToast(scoringRunners);
    if (runsScored > 0) { const innIdx = state.inning - 1; newState.inning_scores[innIdx][state.half] = (newState.inning_scores[innIdx][state.half] || 0) + runsScored; }
    newState.bases = newBases;
    onUpdate(newState);
  };

  // ── Rundown screen ────────────────────────────────────────────────
  if (rundownBase) {
    const runner = state.bases[rundownBase];
    const fieldingTeam = state.half === "top" ? game.home_team : game.away_team;
    const d6Input = "w-10 h-9 text-lg text-center font-mono font-black bg-slate-800/80 border-2 rounded-lg outline-none transition-all";
    const guessInput = "w-9 h-8 text-base text-center font-mono font-bold bg-slate-900/80 border rounded-lg outline-none transition-all";

    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full flex flex-col items-center justify-center gap-2 px-2">
        {/* Header */}
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded bg-orange-500/20 flex items-center justify-center text-[9px] font-black text-orange-400 border border-orange-500/30">RD</div>
          <span className="text-xs font-black text-orange-400">RUNDOWN</span>
          <span className="text-xs text-slate-500">— {runner?.name || 'Runner'} caught in a pickle</span>
        </div>

        {/* Main row: Fielding | Center | Runner */}
        <div className="flex items-center gap-6">
          {/* Fielding — LEFT */}
          <div className="flex flex-col items-center gap-1.5">
            <span className="text-[10px] font-bold text-rose-400">{fieldingTeam.name || 'Fielding'}</span>
            <div className="flex flex-col items-center gap-1">
              <span className="text-[9px] text-slate-500 uppercase tracking-wide">2 Guesses</span>
              <div className="flex items-center gap-1">
                <input type="text" inputMode="numeric" value={rdFieldGuess1}
                  onChange={(e) => { const v = e.target.value; if (v === "" || (/^\d$/.test(v) && parseInt(v) >= 1 && parseInt(v) <= 6)) setRdFieldGuess1(v); }}
                  placeholder="—" className={`${guessInput} border-rose-500/40 text-rose-300 focus:ring-1 focus:ring-rose-500/30`} />
                <input type="text" inputMode="numeric" value={rdFieldGuess2}
                  onChange={(e) => { const v = e.target.value; if (v === "" || (/^\d$/.test(v) && parseInt(v) >= 1 && parseInt(v) <= 6)) setRdFieldGuess2(v); }}
                  placeholder="—" className={`${guessInput} border-rose-500/40 text-rose-300 focus:ring-1 focus:ring-rose-500/30`} />
              </div>
            </div>
            <div className="flex flex-col items-center gap-1">
              <span className="text-[9px] text-slate-500 uppercase tracking-wide">Roll 2 D6</span>
              <div className="flex items-center gap-1">
                <input type="text" inputMode="numeric" value={rdFieldRoll1}
                  onChange={(e) => { const v = e.target.value; if (v === "" || (/^\d$/.test(v) && parseInt(v) >= 1 && parseInt(v) <= 6)) setRdFieldRoll1(v); }}
                  placeholder="—" className={`${d6Input} ${rdRolling ? 'border-rose-500 text-rose-400' : 'border-rose-500/50 text-rose-300 focus:ring-2 focus:ring-rose-500/40'}`} />
                <input type="text" inputMode="numeric" value={rdFieldRoll2}
                  onChange={(e) => { const v = e.target.value; if (v === "" || (/^\d$/.test(v) && parseInt(v) >= 1 && parseInt(v) <= 6)) setRdFieldRoll2(v); }}
                  placeholder="—" className={`${d6Input} ${rdRolling ? 'border-rose-500 text-rose-400' : 'border-rose-500/50 text-rose-300 focus:ring-2 focus:ring-rose-500/40'}`} />
              </div>
            </div>
          </div>

          {/* Center */}
          <div className="flex flex-col items-center gap-2 min-w-[110px]">
            {!rdResult ? (
              <motion.button onClick={rollRundown} disabled={rdRolling}
                className={`px-4 py-1.5 rounded-lg flex items-center gap-1.5 font-bold text-xs transition-all active:scale-95 ${rdRolling ? 'bg-amber-500 text-black' : 'bg-slate-700 hover:bg-orange-600 text-white border border-slate-600 hover:border-orange-500'}`}>
                <motion.div animate={rdRolling ? { rotate: 360 } : {}} transition={{ duration: 0.3, repeat: rdRolling ? Infinity : 0 }}>
                  <Dices size={13} />
                </motion.div>
                Roll All
              </motion.button>
            ) : (
              <div className="flex flex-col items-center gap-1.5">
                {rdResult === 'repeat' ? (
                  <>
                    <div className="rounded-lg py-1 px-3 bg-slate-700 text-slate-300 text-xs font-black">No match — go again!</div>
                    <button onClick={resetRundown} className="px-3 py-1 rounded-md bg-orange-600 hover:bg-orange-500 text-white font-bold text-xs transition-all">Again</button>
                  </>
                ) : (
                  <>
                    <div className={`rounded-lg py-1 px-3 text-white text-xs font-black ${rdResult === 'safe' ? 'bg-emerald-600' : 'bg-red-600'}`}>
                      {rdResult === 'safe' ? 'SAFE!' : 'OUT!'}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button onClick={resetRundown} className="px-2 py-1 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-[10px] transition-all">Re-roll</button>
                      <button onClick={() => applyRundown(rdResult)} className="px-3 py-1 rounded-md bg-amber-500 hover:bg-amber-400 text-black font-bold text-[10px] transition-all">Apply</button>
                    </div>
                  </>
                )}
              </div>
            )}
            {/* GM force controls */}
            <div className="flex items-center gap-1 mt-1">
              <button onClick={() => applyRundown('out')} className="px-2 py-0.5 rounded text-[9px] font-bold border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-all">Force Out</button>
              <button onClick={() => applyRundown('safe')} className="px-2 py-0.5 rounded text-[9px] font-bold border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 transition-all">Force Safe</button>
            </div>
          </div>

          {/* Runner — RIGHT */}
          <div className="flex flex-col items-center gap-1.5">
            <span className="text-[10px] font-bold text-purple-400">Runner</span>
            <div className="flex flex-col items-center gap-1">
              <span className="text-[9px] text-slate-500 uppercase tracking-wide">Guess</span>
              <input type="text" inputMode="numeric" value={rdRunnerGuess}
                onChange={(e) => { const v = e.target.value; if (v === "" || (/^\d$/.test(v) && parseInt(v) >= 1 && parseInt(v) <= 6)) setRdRunnerGuess(v); }}
                placeholder="—" className={`${guessInput} border-purple-500/40 text-purple-300 focus:ring-1 focus:ring-purple-500/30`} />
            </div>
            <div className="flex flex-col items-center gap-1">
              <span className="text-[9px] text-slate-500 uppercase tracking-wide">Roll D6</span>
              <input type="text" inputMode="numeric" value={rdRunnerRoll}
                onChange={(e) => { const v = e.target.value; if (v === "" || (/^\d$/.test(v) && parseInt(v) >= 1 && parseInt(v) <= 6)) setRdRunnerRoll(v); }}
                placeholder="—" className={`${d6Input} ${rdRolling ? 'border-purple-500 text-purple-400' : 'border-purple-500/50 text-purple-300 focus:ring-2 focus:ring-purple-500/40'}`} />
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // ── Steal screen ──────────────────────────────────────────────────
  if (stealRunner) {
    const runner = state.bases[stealRunner];
    const target = nextBase[stealRunner];
    const fieldingTeam = state.half === "top" ? game.home_team : game.away_team;
    const catcher = fieldingTeam.players.find(p => p.position === "C" && !p.subbed_out) || fieldingTeam.players.find(p => p.position === "C");
    const rRoll = parseInt(runnerRoll) || 0;
    const cRoll = parseInt(catcherRoll) || 0;
    const rMgr = parseInt(runnerMgr) || 0;
    const cMgr = parseInt(catcherMgr) || 0;
    const rTotal = rRoll + rMgr;
    const cTotal = cRoll + cMgr;
    const hasMods = rMgr !== 0 || cMgr !== 0;
    const fmtMath = (roll, mgr, total) => {
      const parts = [String(roll)];
      if (mgr > 0) parts.push(`+${mgr}`);
      else if (mgr < 0) parts.push(String(mgr));
      if (parts.length > 1) parts.push(`= ${total}`);
      return parts.join(' ');
    };

    return (
      <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="h-full flex items-center justify-center">
        <div className="flex items-center gap-12">

            {/* Runner column - Left */}
            <div className="flex flex-col items-center gap-2">
              <div className="flex items-center justify-center gap-1.5">
                <div className="w-6 h-6 rounded bg-purple-500/20 flex items-center justify-center text-[9px] font-black text-purple-400 border border-purple-500/30">{baseLabels[stealRunner]}</div>
                <span className="text-xs font-medium text-slate-400 truncate max-w-24">{runner?.name || 'Runner'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 font-bold">D6</span>
                <motion.input
                  type="text" inputMode="numeric" value={runnerRoll}
                  onChange={(e) => { const v = e.target.value; if (v === "" || (/^\d$/.test(v) && parseInt(v) >= 1 && parseInt(v) <= 6)) setRunnerRoll(v); }}
                  placeholder="—"
                  animate={isRolling ? { scale: [1, 1.1, 1] } : {}}
                  transition={{ duration: 0.1, repeat: isRolling ? Infinity : 0 }}
                  className={`w-14 h-12 text-2xl text-center font-mono font-black bg-slate-800/80 border-2 rounded-lg outline-none transition-all ${isRolling ? 'border-purple-500 text-purple-400' : 'border-purple-500/50 text-purple-300 focus:ring-2 focus:ring-purple-500/40'}`}
                />
              </div>
              <div className="flex items-center gap-1">
                <span className="text-[10px] text-slate-500 font-bold">Mgr</span>
                <input
                  type="text" inputMode="numeric" value={runnerMgr}
                  onChange={(e) => { const v = e.target.value; if (v === "" || v === "-" || /^-?\d$/.test(v)) setRunnerMgr(v); }}
                  placeholder="0"
                  className="w-10 h-7 text-center text-xs font-mono font-bold bg-slate-800/50 border border-slate-700/50 rounded-md text-slate-300 outline-none focus:ring-1 focus:ring-slate-500/30 transition-all"
                />
              </div>
            </div>

            {/* Center column — Roll button OR result */}
            <div className="flex flex-col items-center gap-3 min-w-[120px]">
              {!stealResult ? (
                <motion.button
                  onClick={rollSteal} disabled={isRolling}
                  animate={isRolling ? { scale: [1, 1.05, 1] } : {}}
                  transition={{ duration: 0.2, repeat: isRolling ? Infinity : 0 }}
                  className={`px-5 py-2 rounded-lg flex items-center gap-2 transition-all active:scale-95 font-bold text-sm shadow-md ${
                    isRolling
                      ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-black shadow-amber-500/20'
                      : 'bg-gradient-to-r from-slate-700 to-slate-800 hover:from-purple-600 hover:to-purple-700 text-white border border-slate-600 hover:border-purple-500'
                  }`}
                >
                  <motion.div animate={isRolling ? { rotate: 360 } : {}} transition={{ duration: 0.3, repeat: isRolling ? Infinity : 0 }}>
                    <Dices size={16} />
                  </motion.div>
                  Roll D6s
                </motion.button>
              ) : (
                <>
                  {/* Totals */}
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="flex items-center gap-2 text-xs font-mono">
                    {hasMods ? (
                      <>
                        <span className="text-purple-400">{fmtMath(rRoll, rMgr, rTotal)}</span>
                        <span className="text-slate-600">vs</span>
                        <span className="text-rose-400">{fmtMath(cRoll, cMgr, cTotal)}</span>
                      </>
                    ) : (
                      <>
                        <span className="text-purple-400 font-bold">{rTotal}</span>
                        <span className="text-slate-600">vs</span>
                        <span className="text-rose-400 font-bold">{cTotal}</span>
                      </>
                    )}
                  </motion.div>
                  {/* Result badge */}
                  <motion.div
                    initial={{ y: -8, opacity: 0, scale: 0.9 }} animate={{ y: 0, opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: 0.15 }}
                    className={`rounded-lg py-1.5 px-4 text-white text-center ${stealResult === 'safe' ? 'bg-emerald-600' : 'bg-red-600'}`}
                  >
                    <span className="text-sm font-black">{stealResult === 'safe' ? 'SAFE!' : 'OUT!'}</span>
                  </motion.div>
                  {/* Action buttons */}
                  <motion.div initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.25 }} className="flex items-center gap-2">
                    <button onClick={() => { setStealResult(null); setRunnerRoll(""); setCatcherRoll(""); }} className="px-3 py-1 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-xs transition-all">
                      Re-roll
                    </button>
                    <button onClick={() => applySteal(stealResult)} className="px-4 py-1 rounded-md bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs transition-all">
                      Apply
                    </button>
                  </motion.div>
                </>
              )}
            </div>

            {/* Catcher column - Right */}
            <div className="flex flex-col items-center gap-2">
              <div className="flex items-center justify-center gap-1.5">
                <span className="text-xs font-medium text-slate-400 truncate max-w-24">{catcher?.name || 'Catcher'}</span>
                <div className="w-6 h-6 rounded bg-rose-500/20 flex items-center justify-center text-[9px] font-black text-rose-400 border border-rose-500/30">C</div>
              </div>
              <div className="flex items-center gap-2">
                <motion.input
                  type="text" inputMode="numeric" value={catcherRoll}
                  onChange={(e) => { const v = e.target.value; if (v === "" || (/^\d$/.test(v) && parseInt(v) >= 1 && parseInt(v) <= 6)) setCatcherRoll(v); }}
                  placeholder="—"
                  animate={isRolling ? { scale: [1, 1.1, 1] } : {}}
                  transition={{ duration: 0.1, repeat: isRolling ? Infinity : 0 }}
                  className={`w-14 h-12 text-2xl text-center font-mono font-black bg-slate-800/80 border-2 rounded-lg outline-none transition-all ${isRolling ? 'border-rose-500 text-rose-400' : 'border-rose-500/50 text-rose-300 focus:ring-2 focus:ring-rose-500/40'}`}
                />
                <span className="text-xs text-slate-500 font-bold">D6</span>
              </div>
              <div className="flex items-center gap-1">
                <input
                  type="text" inputMode="numeric" value={catcherMgr}
                  onChange={(e) => { const v = e.target.value; if (v === "" || v === "-" || /^-?\d$/.test(v)) setCatcherMgr(v); }}
                  placeholder="0"
                  className="w-10 h-7 text-center text-xs font-mono font-bold bg-slate-800/50 border border-slate-700/50 rounded-md text-slate-300 outline-none focus:ring-1 focus:ring-slate-500/30 transition-all"
                />
                <span className="text-[10px] text-slate-500 font-bold">Mgr</span>
              </div>
            </div>

          </div>
      </motion.div>
    );
  }

  // ── Normal runner list ─────────────────────────────────────────────
  return (
    <motion.div data-testid="baserunning-phase" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full flex flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto">
        {runners.length === 0 ? (
          <div className="h-full flex items-center justify-center text-slate-600 text-sm">No runners on base</div>
        ) : (
          <div className="space-y-1.5">
            {runners.map(base => {
              const runner = state.bases[base];
              const isSelected = selectedRunner === base;
              return (
                <div key={base} className={`rounded-lg px-3 py-2 flex items-center gap-3 transition-all ${isSelected ? 'bg-amber-500/15 ring-1 ring-amber-500/40' : 'bg-slate-800/30 hover:bg-slate-800/50'}`}>
                  <div className={`w-8 h-8 rounded-md flex items-center justify-center font-black text-xs ${isSelected ? 'bg-amber-500 text-black' : 'bg-slate-700 text-slate-300'}`}>
                    {baseLabels[base]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-white truncate">{runner?.name || 'Runner'}</p>
                    <p className="text-[10px] text-slate-500">{baseNames[base]} Base</p>
                  </div>
                  {isSelected ? (
                    <div className="flex items-center gap-1">
                      <button onClick={() => advanceRunner(base, nextBase[base])} className="px-2.5 py-1.5 rounded-md text-[10px] font-bold bg-emerald-600 hover:bg-emerald-500 text-white transition-all">
                        {nextBase[base] === 'home' ? 'Score' : `${baseLabels[nextBase[base]]}`}
                      </button>
                      {canSteal(base) && (
                        <button
                          onClick={() => { setSelectedRunner(null); setStealRunner(base); }}
                          className="px-2.5 py-1.5 rounded-md text-[10px] font-bold bg-purple-700 hover:bg-purple-600 text-white transition-all"
                        >
                          Steal
                        </button>
                      )}
                      <button onClick={() => recordOut(base)} className="px-2.5 py-1.5 rounded-md text-[10px] font-bold bg-red-600 hover:bg-red-500 text-white transition-all">Out</button>
                      <button onClick={() => setSelectedRunner(null)} className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-700 transition-all"><X size={14} /></button>
                    </div>
                  ) : (
                    <button onClick={() => setSelectedRunner(base)} className="p-1.5 rounded-md text-slate-500 hover:text-amber-400 hover:bg-slate-700 transition-all"><ChevronRight size={18} /></button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-3 pb-1 pt-2">
        {runners.length > 0 && (
          <button onClick={advanceAll} className="px-3 py-1.5 rounded-md text-xs font-bold bg-slate-800 hover:bg-slate-700 text-slate-300 transition-all flex items-center gap-1">
            <ArrowUp size={12} /> Advance All
          </button>
        )}
        <button data-testid="ready-next-pitch" onClick={() => { setSelectedRunner(null); setCurrentPhase("pitch"); }}
          className="px-5 py-1.5 rounded-md text-xs font-bold bg-amber-500 hover:bg-amber-400 text-black transition-all">
          Next Pitch
        </button>
      </div>
    </motion.div>
  );
}
